import React from 'react';
import { useParams, Routes, Route } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiCamera, 
  FiUser, 
  FiMail, 
  FiMapPin, 
  FiPhone, 
  FiInstagram, 
  FiFacebook, 
  FiTwitter, 
  FiLinkedin,
  FiMenu
} = FiIcons;

const PortfolioSite = () => {
  const { username } = useParams();

  // Simulated portfolio data
  const portfolio = {
    name: 'John Doe',
    username: 'johndoe',
    title: 'Professional Photographer',
    tagline: 'Capturing moments that last forever',
    bio: 'I am a professional photographer with over 10 years of experience specializing in weddings, portraits, and landscape photography. My passion is to tell stories through my lens and create memories that will last a lifetime.',
    location: 'San Francisco, CA',
    email: 'contact@johndoe.com',
    phone: '+1 (555) 123-4567',
    website: 'www.johndoephotography.com',
    social: {
      instagram: 'johndoephoto',
      facebook: 'johndoephotography',
      twitter: 'johndoephoto',
      linkedin: 'johndoe'
    },
    galleries: [
      {
        id: 1,
        title: 'Wedding Photography',
        description: 'Beautiful moments from special days',
        coverImage: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=600&h=400&fit=crop',
        imageCount: 48
      },
      {
        id: 2,
        title: 'Portrait Sessions',
        description: 'Professional portrait photography',
        coverImage: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=600&h=400&fit=crop',
        imageCount: 36
      },
      {
        id: 3,
        title: 'Landscape Collection',
        description: 'Breathtaking views from around the world',
        coverImage: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=600&h=400&fit=crop',
        imageCount: 24
      },
      {
        id: 4,
        title: 'Urban Exploration',
        description: 'City life and architecture',
        coverImage: 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=600&h=400&fit=crop',
        imageCount: 32
      }
    ]
  };

  const HomePage = () => (
    <div className="space-y-16">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px]">
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40">
          <img 
            src="https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=1200&h=800&fit=crop" 
            alt="Hero" 
            className="w-full h-full object-cover mix-blend-overlay" 
          />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white max-w-3xl px-6">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4"
            >
              {portfolio.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl mb-6"
            >
              {portfolio.title}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-lg"
            >
              {portfolio.tagline}
            </motion.p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="container max-w-6xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          <div className="lg:w-1/2">
            <img 
              src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=600&h=600&fit=crop" 
              alt="Photographer" 
              className="rounded-lg shadow-xl w-full max-w-md mx-auto" 
            />
          </div>
          <div className="lg:w-1/2">
            <h2 className="text-3xl font-bold mb-4">About Me</h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
              {portfolio.bio}
            </p>
            <div className="space-y-3">
              <div className="flex items-center">
                <SafeIcon icon={FiMapPin} className="w-5 h-5 text-primary-600 mr-2" />
                <span>{portfolio.location}</span>
              </div>
              <div className="flex items-center">
                <SafeIcon icon={FiMail} className="w-5 h-5 text-primary-600 mr-2" />
                <span>{portfolio.email}</span>
              </div>
              <div className="flex items-center">
                <SafeIcon icon={FiPhone} className="w-5 h-5 text-primary-600 mr-2" />
                <span>{portfolio.phone}</span>
              </div>
            </div>
            <div className="flex space-x-4 mt-6">
              <a href="#" className="text-gray-600 hover:text-primary-600 transition-colors">
                <SafeIcon icon={FiInstagram} className="w-6 h-6" />
              </a>
              <a href="#" className="text-gray-600 hover:text-primary-600 transition-colors">
                <SafeIcon icon={FiFacebook} className="w-6 h-6" />
              </a>
              <a href="#" className="text-gray-600 hover:text-primary-600 transition-colors">
                <SafeIcon icon={FiTwitter} className="w-6 h-6" />
              </a>
              <a href="#" className="text-gray-600 hover:text-primary-600 transition-colors">
                <SafeIcon icon={FiLinkedin} className="w-6 h-6" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Galleries */}
      <section className="container max-w-6xl mx-auto px-6">
        <h2 className="text-3xl font-bold mb-8 text-center">Featured Galleries</h2>
        <div className="grid md:grid-cols-2 gap-8">
          {portfolio.galleries.map((gallery) => (
            <motion.div
              key={gallery.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="group relative overflow-hidden rounded-lg shadow-lg"
            >
              <div className="aspect-[3/2] bg-gray-200">
                <img 
                  src={gallery.coverImage} 
                  alt={gallery.title} 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" 
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-80 flex flex-col justify-end p-6">
                <h3 className="text-xl font-bold text-white mb-1">
                  {gallery.title}
                </h3>
                <p className="text-white/80 mb-2">
                  {gallery.description}
                </p>
                <p className="text-sm text-white/60">
                  {gallery.imageCount} photos
                </p>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-10">
          <button className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg shadow-lg transition-colors">
            View All Galleries
          </button>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-gray-50 dark:bg-dark-800 py-16">
        <div className="container max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12 text-center">Client Testimonials</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah Johnson',
                photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
                text: "Working with John was an absolute pleasure! He captured our wedding day perfectly and delivered photos that exceeded our expectations."
              },
              {
                name: 'Michael Chen',
                photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
                text: "The corporate headshots John took for our team were professional and really captured everyone's personality. Highly recommended!"
              },
              {
                name: 'Emily Rodriguez',
                photo: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
                text: "John's attention to detail and creative vision made our family portrait session fun and the results are stunning. We'll cherish these photos forever."
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white dark:bg-dark-700 rounded-lg p-6 shadow-md"
              >
                <div className="flex items-center mb-4">
                  <img 
                    src={testimonial.photo} 
                    alt={testimonial.name} 
                    className="w-12 h-12 rounded-full object-cover mr-4" 
                  />
                  <h3 className="font-semibold">{testimonial.name}</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 italic">
                  "{testimonial.text}"
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="container max-w-6xl mx-auto px-6 text-center pb-16">
        <h2 className="text-3xl font-bold mb-4">Ready to Work Together?</h2>
        <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
          I'm available for bookings and would love to discuss your photography needs. Get in touch to schedule a consultation.
        </p>
        <button className="px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg shadow-lg transition-colors text-lg">
          Contact Me
        </button>
      </section>
    </div>
  );

  return (
    <div className="bg-white dark:bg-dark-900 min-h-screen">
      {/* Navigation */}
      <nav className="bg-white/80 dark:bg-dark-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 sticky top-0 z-50">
        <div className="container max-w-6xl mx-auto px-6">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">
                  {portfolio.name.charAt(0)}
                </span>
              </div>
              <span className="text-xl font-bold">
                {portfolio.name}
              </span>
            </div>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#" className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                Home
              </a>
              <a href="#" className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                Portfolio
              </a>
              <a href="#" className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                About
              </a>
              <a href="#" className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                Contact
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button className="md:hidden p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <SafeIcon icon={FiMenu} className="w-6 h-6" />
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
      </main>

      {/* Footer */}
      <footer className="bg-gray-100 dark:bg-dark-800 py-12">
        <div className="container max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">
                    {portfolio.name.charAt(0)}
                  </span>
                </div>
                <span className="text-xl font-bold">
                  {portfolio.name}
                </span>
              </div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {portfolio.tagline}
              </p>
            </div>
            <div className="flex space-x-6">
              <a href="#" className="text-gray-600 hover:text-primary-600 transition-colors">
                <SafeIcon icon={FiInstagram} className="w-6 h-6" />
              </a>
              <a href="#" className="text-gray-600 hover:text-primary-600 transition-colors">
                <SafeIcon icon={FiFacebook} className="w-6 h-6" />
              </a>
              <a href="#" className="text-gray-600 hover:text-primary-600 transition-colors">
                <SafeIcon icon={FiTwitter} className="w-6 h-6" />
              </a>
              <a href="#" className="text-gray-600 hover:text-primary-600 transition-colors">
                <SafeIcon icon={FiLinkedin} className="w-6 h-6" />
              </a>
            </div>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700 mt-8 pt-8 text-center text-gray-500 dark:text-gray-400">
            <p>&copy; {new Date().getFullYear()} {portfolio.name}. All rights reserved.</p>
            <p className="text-sm mt-2">
              Powered by <span className="text-primary-600">Tasveer</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PortfolioSite;